import { Checkbox, Select } from "antd";
import React, { useCallback } from "react";
import InputNumber from "../../../../../components/Basic/InputNumber";
import ValueAndClear from "../../../../../components/Basic/ValueAndClear";
import Icon, { ICON_NAME } from "../../../../../components/Icon";
import Menu from "../../../../../components/Menu";
import { cloneObj, renderOpts } from "../../../../../util/varibles/global";
import { IDeliveryTypeUnit, IUnit } from "../../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../../util/varibles/message";
import Tanks from "../_components/Tanks";
import { getEditSubOp, useEditSubOp } from "../EditOperationContext/EditSubOperation";
import { DELIVERY_UNIT, deliveryUnits, initDeliveryType } from "./constants";
import styles from "./style.module.scss";

interface IInputValue {
    data: any;

    onChange(value: { fish_amount: number, total_weight: number }): void;
}

const InputTons: React.FC<IInputValue> = ({ data, onChange }) => {
    const [harvestUnit] = useEditSubOp(state => state.unit);
    const { avg_weight } = harvestUnit;
    const isExternal = avg_weight === -1;
    const avgWeight = Math.max(avg_weight, 1);

    const handleChange = useCallback((value: number) => {
        const total_weight = Math.round(value * 1000000);
        const fish_amount = !isExternal ? Math.round(total_weight / avgWeight) : 0;
        onChange({ fish_amount, total_weight });
    }, [avgWeight, isExternal, onChange]);

    return <InputNumber
        className="text-right pr-2 w-full"
        value={Math.round(data.total_weight / 1000000)}
        onChange={handleChange}
    />;
};

const InputAmount: React.FC<IInputValue> = ({ data, onChange }) => {
    const [avg_weight] = useEditSubOp(state => state.unit.avg_weight);
    const avgWeight = Math.max(avg_weight, 1);

    const handleChange = useCallback((value: number) => {
        const fish_amount = Math.round(value);
        const total_weight = Math.round(fish_amount * avgWeight);
        onChange({ fish_amount, total_weight });
    }, [avgWeight, onChange]);

    return <InputNumber
        className="text-right pr-2 w-full"
        value={Math.round(data.fish_amount)}
        onChange={handleChange}
    />;
};

interface IDeliveryTypeProps {
    data: IDeliveryTypeUnit;

    onChange(value: Partial<IDeliveryTypeUnit>, isCalculate?: boolean): void;

    onDelete(): void;
}

const DeliveryType: React.FC<IDeliveryTypeProps> = ({ data, onChange, onDelete }) => {
    const [harvestUnit] = useEditSubOp(state => state.unit);
    const { avg_weight } = harvestUnit;
    const isExternal = avg_weight === -1;
    const { unit = DELIVERY_UNIT.TONS } = data;
    const get = getEditSubOp();

    const handleChange = useCallback((value: Partial<IDeliveryTypeUnit>) => {
        const newValue = { ...data, ...value };
        const { tanks = [] } = newValue;
        const { remaining_weight, unit } = get();
        const { avg_weight } = unit;
        const { total_weight: oldTotalWeight = 0 } = data
        const { total_weight: newTotalWeight = 0 } = newValue;
        if ((newTotalWeight - oldTotalWeight) > remaining_weight) {
            notify.error(NotifyCode.E1)();
            const avgWeight = Math.max(avg_weight, 1);
            newValue.fish_amount = isExternal ? 0 : Math.round((oldTotalWeight + remaining_weight) / avgWeight);
            newValue.total_weight = isExternal ? oldTotalWeight + remaining_weight : newValue.fish_amount * avgWeight;
        }
        newValue.tanks = tanks.map(({ total_weight: _, ...args }) => args)

        onChange(newValue, true)
    }, [get, data, isExternal, onChange]);

    return <div className={styles.subLine}>
        <div className={styles.deliveryMethod}>
            <ValueAndClear onClear={onDelete}>
                <span className='whitespace-nowrap'>{data.name}</span>
            </ValueAndClear>
            {isExternal || unit === DELIVERY_UNIT.TONS
                ? <InputTons data={data} onChange={value => handleChange(value)} />
                : <InputAmount data={data} onChange={value => handleChange(value)} />}
            <Select
                className='min-w-[80px]'
                variant='borderless'
                value={isExternal ? DELIVERY_UNIT.TONS : unit || DELIVERY_UNIT.TONS}
                classNames={{ popup: { root: "no-scroll" } }}
                popupMatchSelectWidth={false}
                options={renderOpts(!isExternal ? deliveryUnits : [deliveryUnits[0]])}
                onChange={value => onChange({ ...data, unit: value })}
            />
        </div>
        <Tanks data={data.tanks} onChange={value => onChange({ ...data, tanks: value }, true)} />
    </div>;
};

interface IProps {
    data: IDeliveryTypeUnit[];

    onChange(value: Partial<IUnit>, isCalculate?: boolean): void;
}

const DeliveryTypes: React.FC<IProps> = ({ data, onChange }) => {
    const [deliveryTypes] = useEditSubOp(state => state.deliveryTypes);

    const handleChange = useCallback((index: number, value: Partial<IDeliveryTypeUnit>, isCalculate?: boolean) => {
        const newValue = cloneObj(data);
        newValue[index] = { ...newValue[index], ...value };
        onChange({ delivery_types: newValue }, isCalculate);
    }, [data, onChange]);

    const ids = new Set(data.map(item => item.id));

    const handleDelete = useCallback((id: string) => {
        onChange({ delivery_types: data.filter(item => item.id !== id) }, true);
    }, [data, onChange]);

    const handleChangeDeliveryTypes = useCallback((checked: boolean, value: string) => {
        if (checked) {
            onChange({
                delivery_types: [...data || [], initDeliveryType(deliveryTypes[value])]
            }, false);
        } else {
            handleDelete(value)
        }
    }, [deliveryTypes, onChange, data, handleDelete]);

    return <div>
        <div>
            <Menu
                placement='bottom'
                classNames={{ root: 'max-w-[220px] w-full' }}
                items={[{
                    key: "1",
                    className: "no-hover",
                    label: <div className='flex flex-col gap-3 py-1'>
                        {Object.keys(deliveryTypes).map(key => {
                            const item = deliveryTypes[key];
                            return <Checkbox
                                key={key}
                                checked={ids.has(key)}
                                onChange={({ target }) => handleChangeDeliveryTypes(target.checked, key)}
                            >
                                {item.name}
                            </Checkbox>;
                        })}
                    </div>,
                    disabled: true
                }]}
            >
                <div className="flex items-center gap-2 cursor-pointer w-fit">
                    <span>Delivery methods</span>
                    <Icon size={10} icon={ICON_NAME.ARROW_DOWN} />
                </div>
            </Menu>
        </div>
        {data.map((item, i) => <DeliveryType
            key={item.id}
            data={item}
            onChange={(value, isCalculate) => handleChange(i, value, isCalculate)}
            onDelete={() => handleDelete(item.id || '')}
        />)}
    </div>;
};

export default DeliveryTypes;
